import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { FaSlack } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';

export default function WorkspaceCards() {
  const [workspaces, setWorkspaces] = useState([]);
  const [messages, setMessages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Fetch workspaces on mount
  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        setError(null);
        const token = Cookies.get('token');
        if (!token) {
          throw new Error('Please log in to view workspaces.');
        }

        const res = await axios.get(`${apiUrl}/api/slack/workspaces`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.data.connectedWorkspaces) {
          throw new Error('Failed to fetch workspaces.');
        }

        setWorkspaces(res.data.connectedWorkspaces);
      } catch (err) {
        console.error('Error fetching workspaces:', err.message);
        setError(err.message || 'Failed to fetch workspaces. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchWorkspaces();
  }, []);

  //?

  const fetchMessages = async (teamId) => {
    try {
      setError(null);
      const token = Cookies.get('token');
      if (!token) {
        throw new Error('Please log in to fetch messages.');
      }

      const res = await axios.get(`${apiUrl}/api/slack/messages?teamId=${teamId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.data.workspaces) {
        throw new Error('Failed to fetch messages.');
      }

      setMessages((prev) => ({
        ...prev,
        [teamId]: res.data.workspaces.find((ws) => ws.teamId === teamId)?.messages || [],
      }));
    } catch (err) {
      console.error('Error fetching messages:', err.message);
      setError(err.message || 'Failed to fetch messages. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
          {error.includes('log in') && (
            <Button as="a" href="/auth" className="ms-2" variant="link">
              Log In
            </Button>
          )}
        </Alert>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <h2 className="mb-4 text-center">Connected Workspaces</h2>
      {workspaces.length === 0 ? (
        <Alert variant="info">
          No workspaces connected. <a href="/auth">Connect a workspace</a>.
        </Alert>
      ) : (
        <Row xs={1} md={2} lg={3} className="g-4">
          {workspaces.map((workspace) => (
            <Col key={workspace.teamId}>
              <Card className="shadow-sm h-100">
                <Card.Body className="d-flex flex-column">
                  <div className="text-center mb-3">
                    <FaSlack size={40} className="text-dark" />
                  </div>
                  <Card.Title className="text-center">{workspace.teamName}</Card.Title>
                  <Card.Title className="text-center mb-3">Slack Worksapce</Card.Title>
                  <Button
                    variant="primary"
                    className="mt-auto"
                    disabled
                    onClick={() => fetchMessages(workspace.teamId)}
                  >
                    Connected...
                  </Button>
                  {messages[workspace.teamId] && messages[workspace.teamId].length > 0 && (
                    <div className="mt-3">
                      <h6>Messages:</h6>
                      {messages[workspace.teamId].map((channel) => (
                        <div key={channel.channelId}>
                          <h6>{channel.channelName}</h6>
                          <ul>
                            {channel.messages.map((msg) => (
                              <li key={msg.ts}>{msg.text}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
}